import type { TFunction } from 'i18next'
import { z } from 'zod'

import {
  MaxNoteLength,
  MeaningfulNoteLength,
  OptionalNoteSchema,
} from '@mntn-dev/domain-types'

import { isFormattedTextLengthValid } from '../refinements/formatted-text.ts'

export const BrandNoteUpdateFormModelSchema = (
  t: TFunction<'validation'>,
  options?: { filesAttached?: boolean; fileRequired?: boolean }
) => {
  const requiredNote = z.string().superRefine((val, ctx) =>
    isFormattedTextLengthValid(t, t('brand-feedback-form.note.label'), {
      min: MeaningfulNoteLength,
      max: MaxNoteLength,
    }).refinement(val, ctx)
  )

  return z
    .discriminatedUnion('isRequired', [
      z.object({
        isRequired: z.literal(true),
        note: options?.filesAttached ? OptionalNoteSchema : requiredNote,
      }),
      z.object({ isRequired: z.literal(false), note: OptionalNoteSchema }),
    ])
    .superRefine((_val, ctx) => {
      if (options?.fileRequired && !options?.filesAttached) {
        ctx.addIssue({
          code: 'custom',
          message: t('brand-feedback-form.files.label'),
          path: ['note'],
        })
      }
    })
}

export const BrandFeedbackFormModelSchema = (
  t: TFunction<'validation'>,
  options?: { filesAttached?: boolean }
) => {
  const requiredNote = z.string().superRefine((val, ctx) =>
    isFormattedTextLengthValid(t, t('brand-feedback-form.note.label'), {
      min: MeaningfulNoteLength,
      max: MaxNoteLength,
    }).refinement(val, ctx)
  )

  const changeRequestSchema = z.object({
    isChangeRequest: z.literal(true),
    note: OptionalNoteSchema,
  })

  const approvalSchema = z.object({
    isChangeRequest: z.literal(false),
    note: OptionalNoteSchema,
  })

  return z
    .discriminatedUnion('isChangeRequest', [
      changeRequestSchema,
      approvalSchema,
    ])
    .superRefine((val, ctx) => {
      if (val.isChangeRequest && !options?.filesAttached) {
        const noteValidation = requiredNote.safeParse(val.note)
        if (!noteValidation.success) {
          for (const issue of noteValidation.error.issues) {
            ctx.addIssue({ ...issue, path: ['note'] })
          }
        }
      }
    })
}

export const MakerProposalUpdateFormModelSchema = (
  t: TFunction<'validation'>,
  options?: { filesAttached?: boolean }
) => {
  const requiredNote = z.string().superRefine((val, ctx) =>
    isFormattedTextLengthValid(t, t('maker-proposal-form.note.label'), {
      min: MeaningfulNoteLength,
      max: MaxNoteLength,
    }).refinement(val, ctx)
  )

  return z.object({
    note: options?.filesAttached ? OptionalNoteSchema : requiredNote,
  })
}
